/**
 * Copyright (C) 2019 User X, LLC http://whoisuserx.com <info@userx.co>
 *
 * This program is the intellectual property of User X, LLC. You may
 * not redistribute and/or modify it without written consent by controlling
 * entities of User X, LLC.
 *
 * You should have received a copy of our Web Developer Agreement which
 * outlines proper use and distribution of this program. If you did not
 * please email info@userx.co to request your copy.
 */
import Autocomplete from "react-widgets/Autocomplete";
import {useEffect, useRef, useState} from "react";
import supportAPI from "../../_apis/supportAPI";
import useDefaultsManager from "../../_hooks/useDefaultsManager";
import {sendCrmMessages} from "../../_utilities/contactUtilities";
import MarkdownInput from "./formElements/MarkdownInput";
import MarkdownIt from "markdown-it";
import {search} from "../../_utilities/reactUtils";

/**
 * File created by John M. Woodcock < john@userx.co >
 * on 7/25/2025
 * for userx-support
 *
 * @todo The save template part isn't working. Probably due to the Markdown.
 */

const AdminMessaging = () => {
    const [recipientType, setRecipientType] = useState(null);
    const [contactList, setContactList] = useState([]);
    const [agentValue, setAgentValue] = useState(null);
    const [agentList, setAgentList] = useState([]);
    const [companyValue, setCompanyValue] = useState(null);
    const [companyList, setCompanyList] = useState([]);
    const [showTemplates, setShowTemplates] = useState(false);
    const [showTemplateSaver, setShowTemplateSaver] = useState(false);
    const [newTemplateName, setNewTemplateName] = useState('');
    const [templateList, setTemplateList] = useState([]);
    const [subject, setSubject] = useState('');
    const [messageMd, setMessageMd] = useState('');
    const [noticeMessage, setNoticeMessage] = useState(''); // success notice shown after send
    const formRef = useRef(null);
    const status = useDefaultsManager('status-defaults');
    const modes = useDefaultsManager('mode-defaults');
    const services = useDefaultsManager('service-defaults');
    const [errorMessage, setErrorMessage] = useState('');
    const [isSending, setIsSending] = useState(false);

    // helpers (top of file)
    const dedupeBy = (arr, keyFn) => {
        const seen = new Set();
        const out = [];
        for (const item of arr) {
            const key = keyFn(item);
            if (!seen.has(key)) {
                seen.add(key);
                out.push(item);
            }
        }
        return out;
    };
    const md = new MarkdownIt({ html: false, linkify: true, typographer: true });

    const renderTokens = (tpl, tokens) =>
        (tpl || '').replace(/{{\s*([\w]+)\s*}}/g, (_, k) =>
            Object.prototype.hasOwnProperty.call(tokens, k) ? String(tokens[k] ?? '') : ''
        );

    const isPrimary = (co) => co && (co.primary === '1' || co.primary === 1 || co.primary === true);

    const tokenCatalog = {
        clients: [
            { key: 'companyName', label: 'Company Name' },
            { key: 'companyId',   label: 'Company ID' },
            { key: 'primaryFirst',label: 'Primary First' },
            { key: 'primaryLast', label: 'Primary Last' },
            { key: 'primaryEmail',label: 'Primary Email' },
            { key: 'primaryPhone',label: 'Primary Phone' },
        ],
        agents: [
            { key: 'agentFirst',  label: 'Agent First' },
            { key: 'agentLast',   label: 'Agent Last' },
            { key: 'agentEmail',  label: 'Agent Email' },
        ],
        tickets: [
            { key: 'companyName', label: 'Company Name' },
            { key: 'contactFirst',label: 'Contact First' },
            { key: 'contactLast', label: 'Contact Last' },
            { key: 'ticketList', label: 'Ticket List'}
        ],
    };
    const editorRef = useRef(null);

    useEffect(() => {
        status.fetchDefaults();
        modes.fetchDefaults();
        services.fetchDefaults();
        supportAPI.get(`/contact`).then(response => {
            if (response.data) {
                setContactList(response.data);
                setAgentList(response.data.filter(c => parseInt(c.group_id) < 5));
            }
        });
        supportAPI.get(`/company`).then(response => {
            if (response.data) {
                setCompanyList(() => response.data);
            }
        });
        supportAPI.get(`/sendmail/templates`).then(response => {
            if (response.data) {
                const raw = response.data;
                const normalized = Array.isArray(raw)
                    ? raw.reduce((acc, t) => {
                        const name = t.name || t.key || t.id || `template_${t.id}`;
                        acc[name] = {id: t.id, subject: t.subject ?? '', message: t.message ?? ''};
                        return acc;
                    }, {})
                    : Object.entries(raw).reduce((acc, [name,t]) => {
                        acc[name] = {id: t.id, subject: t.subject ?? '', message: t.message ?? ''};
                        return acc;
                    }, {});

                setTemplateList(normalized);
            }
        });
    }, []);

    const handleTemplateChange = (e) => {
        const name = e.target.value;
        setNewTemplateName(name);
        const tpl = templateList[name];
        if (tpl) {
            setSubject(tpl.subject ?? '');
            setMessageMd(tpl.message ?? '');
        }
        setShowTemplates(false);
    };

    const handleSaveTemplate = async () => {
        const name = (newTemplateName || '').trim();
        if (!name) {
            setErrorMessage(
                <div className="alert alert-danger py-2 px-3 my-2">
                    <i className="bi bi-exclamation-fill text-danger" /> Enter a template name.
                </div>
            );
            return;
        }

        try {
            const body = {
                name: String(name),
                subject: String(subject ?? ''),
                // Save the **Markdown** raw; you render to HTML later when sending
                message: String(messageMd ?? '')
            };

            const response = await supportAPI.post(`/sendmail/templates/${encodeURIComponent(name)}`, body);

            // Update local cache as a map
            const saved = response?.data ?? body;
            setTemplateList(prev => ({
                ...prev,
                [name]: {
                    id: saved.id ?? prev[name]?.id,
                    subject: saved.subject ?? body.subject,
                    message: saved.message ?? body.message
                }
            }));

            setNewTemplateName('');
            setSubject('');
            setMessageMd('');
            setShowTemplateSaver(false);
            setErrorMessage('');
        } catch (err) {
            console.error('handleSaveTemplate::ERROR', err);
            setErrorMessage(
                <div className="alert alert-danger py-2 px-3 my-2">
                    <i className="bi bi-exclamation-fill text-danger" /> {err?.message || 'Failed to save template.'}
                </div>
            );
        }
    };

    const insertToken = (txt) => {
        if (editorRef.current?.insertText) {
            editorRef.current.insertText(txt);
        } else {
            setMessageMd(m => (m ? `${m} ${txt}` : txt));
        }
    };

    // Reset the form and controlled state (manual action)
    const handleReset = () => {
        setRecipientType(null);
        setAgentValue(null);
        setCompanyValue(null);
        setSubject('');
        setMessageMd('');
        setNewTemplateName('');
        setShowTemplates(false);
        setShowTemplateSaver(false);
        setErrorMessage('');
        setNoticeMessage('');
        try { formRef.current?.reset(); } catch(e) { /* ignore */ }
        // also clear editor if it exposes a value setter
        if (editorRef.current?.setValue) try { editorRef.current.setValue(''); } catch(e) {/* ignore */}
    };


    const handleSend = async (e) => {
        e.preventDefault();
        setIsSending(true);
        setNoticeMessage(()=>'');
        setErrorMessage(()=>'');
        try {
            let tickets = [];
            let sendTo = [];
            switch(recipientType){
                case 'clients': {
                    const modeVal = document.getElementById('companyMode')?.value?.trim() || '';
                    const serviceVal = document.getElementById('serviceType')?.value?.trim() || '';

                    // helpers to match company meta flexibly (handles common key names and CSV values)
                    const hasMetaValue = (company, keys, val) => {
                        if (!val) return true; // no restriction
                        return (company.meta ?? []).some(m =>
                            keys.includes(m.meta_key) &&
                            String(m.meta_value)
                            .split(',')        // allow comma-separated values
                                .map(s => s.trim())
                                .includes(String(val))
                        );
                    };

                    const matchesMode = (company) => hasMetaValue(company, ['mode', 'status'], modeVal);
                    const matchesService = (company) => hasMetaValue(company, ['service', 'service_id', 'services'], serviceVal);

                    // If either mode or service selected, compute allowed companies
                    const needFilter = Boolean(modeVal || serviceVal);
                    const allowedCompanyIds = needFilter
                        ? new Set(
                            companyList
                            .filter(c => matchesMode(c) && matchesService(c))
                            .map(c => c.company_id)
                        )
                        : null;

                    if (companyValue) {
                        // Specific company selected: ignore mode/service filters, always send to its primary
                        sendTo = contactList
                        .flatMap(c =>
                            (c.company ?? [])
                            .filter(co => co.company_id === companyValue.company_id && isPrimary(co))
                            .map(co => ({
                                contact_id: c.contact_id,
                                company_id: co.company_id,
                                ticket_id: null
                            }))
                        );
                    } else {
                        // No specific company: filter primaries by optional mode/service
                        sendTo = contactList
                        .flatMap(c =>
                            (c.company ?? [])
                            .filter(co =>
                                isPrimary(co) &&
                                (!needFilter || allowedCompanyIds.has(co.company_id))
                            )
                            .map(co => ({
                                contact_id: c.contact_id,
                                company_id: co.company_id,
                                ticket_id: null
                            }))
                        );
                    }

                    // de-dupe (one message per contact+company)
                    sendTo = dedupeBy(sendTo, x => `${x.contact_id}:${x.company_id}`);
                    break;
                }
                case 'agents':
                    if (agentValue) {
                        sendTo = [{ contact_id: agentValue.contact_id, company_id: null }];
                    } else {
                        sendTo = agentList.map(a => ({
                            contact_id: a.contact_id,
                            company_id: null,
                            ticket_id: null
                        }));
                    }
                    break;
                case 'tickets':
                    const ticketContact = document.getElementById('ticketContact').value;
                    const ticketStatus = document.getElementById('ticketStatus').value;
                    if(ticketContact === '' || ticketStatus === ''){
                        throw new Error('You must choose a contact type and ticket status for tickets.');
                    }
                    const filters = ['status_id != 5','status_id != 6',`status_id = ${ticketStatus}`];
                    tickets = await search('ticket', filters);

                    sendTo = tickets.map(t => ({
                        contact_id: t[ticketContact],
                        company_id: t.company_id,
                        ticket_id: t.ticket_id
                    }));
                    break;
                default:

            }

            // de-dupe
            sendTo = [...new Set(sendTo)];

            // Build lookup maps once
            const contactById = new Map(contactList.map(c => [String(c.contact_id), c]));
            const companyById = new Map(companyList.map(c => [String(c.company_id), c]));
            const ticketById  = new Map(tickets.map(t => [String(t.ticket_id), t]));

// Build per-recipient payloads
            let payloads = [];

            if (recipientType === 'tickets') {
                // Group all sendTo rows by contact_id
                const groups = new Map();
                for (const item of sendTo) {
                    const key = String(item.contact_id);
                    if (!groups.has(key)) groups.set(key, []);
                    groups.get(key).push(item);
                }

                console.log('handleSend::ticketGroups',groups);

                for (const [contactKey, items] of groups) {
                    const contact = contactById.get(contactKey);
                    const company = companyById.get(items[contactKey].company_id);
                    // Collect all tickets for this contact (optionally dedupe company_id or render it per ticket)
                    const ticketItems = items
                    .map(i => ticketById.get(String(i.ticket_id)))
                    .filter(Boolean);

                    // Build tokens; ticketList as Markdown bullets
                    const ticketListMd = ticketItems.map(t =>
                        `- **#${t.ticket_id}** ${t.title || ''} (${company.name})`
                    ).join('\n');

                    // If you also want the company context, you can choose one or show per-ticket company:
                    // const company = companyById.get(String(items[0]?.company_id));
                    const tokens = {
                        contactFirst: contact?.first_name || '',
                        contactLast:  contact?.last_name  || '',
                        ticketList:   ticketListMd,
                        // companyName: company?.name || ''  // if you decide to pick one
                    };

                    console.log('handleSend::tokens', tokens);

                    const personalizedMd  = renderTokens(messageMd, tokens);
                    const personalizedHtml = md.render(personalizedMd);

                    payloads.push({
                        contact_id: Number(contactKey),
                        htmlBody: personalizedHtml,
                        subject   // you can also render tokens in subject if you want
                    });
                }
            } else {
                // Clients / Agents: personalize per {contact_id, company_id}
                for (const item of sendTo) {
                    const contact = contactById.get(String(item.contact_id));
                    const company = item.company_id != null
                        ? companyById.get(String(item.company_id))
                        : null;

                    const tokens =
                              recipientType === 'clients'
                                  ? {
                                      companyName: company?.name || '',
                                      companyId:   company?.company_id ?? '',
                                      primaryFirst: contact?.first_name || '',
                                      primaryLast:  contact?.last_name  || '',
                                      primaryEmail: contact?.email || '',
                                      primaryPhone: contact?.phone || ''
                                  }
                                  : {
                                      agentFirst: contact?.first_name || '',
                                      agentLast:  contact?.last_name  || '',
                                      agentEmail: contact?.email || ''
                                  };

                    const personalizedMd  = renderTokens(messageMd, tokens);
                    const personalizedHtml = md.render(personalizedMd);

                    payloads.push({
                        contact_id: item.contact_id,
                        htmlBody: personalizedHtml,
                        subject
                    });
                }
            }

            const report = await sendCrmMessages(payloads);
            console.log('handleSend::report', report);
            // show persistent success notice (do not auto-reset the form)
            const sentCount = Array.isArray(payloads) ? payloads.length : (report?.sentCount ?? 'unknown');
            setNoticeMessage(
                <div className="alert alert-success py-2 px-3 my-2">
                    <i className="bi bi-check-circle text-success" /> {`Messages sent: ${sentCount}`}
                </div>
            );
            setErrorMessage('');
         } catch(err) {
             console.error('handleSend::ERROR', err);
             setErrorMessage(<div className={`alert alert-danger py-2 px-3 my-2`}><i className={`bi bi-exclamation-fill text-danger`} />{err.message}</div>)
         } finally {
            setIsSending(false);
        }

     };

     return (
         <div className="component-container d-flex flex-column h-100">
             <h4>Message Center</h4>
             <p>Send messages to contacts with the Support System.</p>
             <form className={`my-3`}
                   onSubmit={handleSend}
                   ref={formRef}
             >
                <label className={'form-label'}>To:</label>
                <select className={`form-select`}
                        onChange={(e) => setRecipientType(e.target.value)}
                >
                    <option>Select recipient(s)...</option>
                    <option value={`contacts`}>All Contacts</option>
                    <option value={`clients`}>Clients</option>
                    <option value={`agents`}>Agents</option>
                    <option value={`tickets`}>Tickets</option>
                </select>
                {recipientType === 'clients'
                    ? <div className={`mt-2 d-flex align-items-center`}>
                        <i className={`ms-3 me-1 fs-2 text-secondary bi bi-arrow-return-right`}/>
                        <select id={`companyMode`} className={`form-select me-1`}
                                style={{maxWidth: '200px'}}
                        >
                            <option value={null}>Mode (optional)...</option>
                            {modes.defaults.map(s => <option key={`option-${s.name}`} value={s.id}>{s.name}</option>)}
                        </select>
                        <select id={`serviceType`} className={`form-select me-1`}
                                style={{maxWidth: '200px'}}
                        >
                            <option value={null}>Service (optional)...</option>
                            {services.defaults.map(s => <option key={`option-${s.name}`} value={s.id}>{s.name}</option>)}
                        </select>
                        <Autocomplete
                            className={`flex-grow-1`}
                            data={companyList}
                            value={companyValue}
                            onChange={(val) => {
                                setCompanyValue(val);
                            }}
                            textField="name"
                            placeholder="Search for a company (optional)"
                        /></div>
                    : null
                }
                {recipientType === 'agents'
                    ? <div className={`mt-2 d-flex align-items-center`}>
                        <i className={`ms-3 me-1 fs-2 text-secondary bi bi-arrow-return-right`}/>
                        <Autocomplete
                            className={`flex-grow-1`}
                            data={agentList}
                            value={agentValue}
                            onChange={(val) => {
                                setAgentValue(val);
                            }}
                            textField="first_name"
                            placeholder="Search for an agent (optional)"
                        />
                    </div>
                    : null
                }
                {recipientType === 'tickets'
                    ? <div className={`mt-2 d-flex align-items-center`}>
                        <i className={`ms-3 me-1 fs-2 text-secondary bi bi-arrow-return-right`}/>
                        <select id={`ticketContact`} className={`form-select me-1`}
                                style={{maxWidth: '200px'}}
                        >
                            <option value={''}>Select Contact Type...</option>
                            <option value={'contact_id'}>Owner</option>
                            <option value={'assignee_id'}>Agent</option>
                        </select>
                        <select id={`ticketStatus`} className={`form-select`}
                                style={{maxWidth: '200px'}}
                        >
                            <option value={''}>Select Ticket Status...</option>
                            {status.defaults.filter(s => (s.name !== 'Complete' && s.name !== 'Cancelled')).map(s => <option key={`status-${s.id}`} value={s.id}>{s.name}</option>)}
                        </select>
                    </div>
                    : null
                }
                <label className={'form-label mt-3'}>Subject:</label>
                <div className={'d-flex align-items-center position-relative'}>
                    <input type={`text`}
                           className={`form-control me-2`}
                           id={'subject'}
                           placeholder={`Subject here...`}
                           value={subject}
                           onChange={(e) => setSubject(e.target.value)}
                    />
                    <div className="d-flex btn btn-secondary py-0" style={{lineHeight: '37px'}}>
                        {templateList && Object.keys(templateList).length > 0 && showTemplates && (
                            <select
                                id="template-selector"
                                className="form-select-sm me-2 text-capitalize"
                                style={{ width: '200px' }}
                                onChange={handleTemplateChange}
                                value={newTemplateName}
                            >
                                <option value="">Select template...</option>
                                {Object.keys(templateList).map(name => (
                                    <option key={templateList[name].id ?? name} value={name}>
                                        {name.replace(/_/g, ' ')}
                                    </option>
                                ))}
                            </select>
                        )}
                        <i className="bi bi-files"
                           title={'Select template'}
                           onClick={(e) => {
                               e.preventDefault();
                               setShowTemplates(!showTemplates);
                           }}/>
                    </div>
                </div>
                <label className={'form-label mt-3'}>Message:</label>
                <MarkdownInput
                    value={messageMd}
                    changeHandler={setMessageMd}
                    height={'260px'}
                    forwardedRef={editorRef}
                    />
                <TokenPalette
                    tokens={tokenCatalog[recipientType] ?? []}
                    onInsert={insertToken}
                />
                <div className={`d-flex justify-content-between align-items-center position-relative mt-3 `}>
                     <div className="d-flex btn btn-success py-0" style={{lineHeight: '37px'}}>
                         <i className="bi bi-files"
                            title={`Save Template`}
                            onClick={() => {
                                setShowTemplateSaver(!showTemplateSaver);
                            }}/>
                         {showTemplateSaver ? <>
                                 <input
                                     className={'form-control py-0 ms-3'}
                                     value={newTemplateName}
                                     placeholder={`Enter template name...`}
                                     onChange={(e) => setNewTemplateName(e.target.value)}
                                 />
                                 <i className="bi bi-check fs-3 mx-2" onClick={handleSaveTemplate}></i>
                                 <i className="bi bi-x fs-3" onClick={() => setShowTemplateSaver(!showTemplateSaver)}></i>
                             </>
                             : null}


                     </div>
                    <div className="d-flex align-items-center ms-auto">
                        <input type={`submit`}
                               className={`btn btn-success me-2 d-block`}
                               value={isSending ? 'Sending...' : 'Send Message'}
                               disabled={isSending}
                        />
                        <button type="button" className="btn btn-secondary" onClick={handleReset}>Reset</button>
                    </div>
                 </div>
                {errorMessage}
                {noticeMessage}
             </form>
         </div>
     );

     function TokenPalette({ tokens, onInsert }) {
         return (
             <div className="d-flex flex-wrap gap-2 mt-2">
                 {tokens.map(t => (
                     <button
                         type="button"
                         key={t.key}
                         className="btn btn-sm btn-outline-secondary"
                         title={t.key}
                         onClick={() => onInsert(`{{${t.key}}}`)}
                     >
                         {t.label}
                     </button>
                 ))}
             </div>
         );
     }
};

export default AdminMessaging
